/**
 * 插入图片对话框
 * 支持资源库、上传、网图三种方式
 */
import React, { useState, useEffect } from 'react';

interface ImageDialogProps {
  show: boolean;
  onClose: () => void;
  onInsert: (urls: string[]) => void;
  selectedText?: string;
  businessLicenseUrl?: string; // 新增：公司营业执照URL
}

type TabType = 'upload' | 'url';  // 暂时隐藏资源tab

// 资源库预设图片（已移除，只显示营业执照）
const RESOURCE_IMAGES: string[] = [];

const ImageDialog: React.FC<ImageDialogProps> = ({
  show,
  onClose,
  onInsert,
  selectedText = '',
  businessLicenseUrl, // 新增
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('upload');  // 默认选择上传tab
  const [selectedResourceImages, setSelectedResourceImages] = useState<Set<number>>(new Set());
  const [uploadedFiles, setUploadedFiles] = useState<Array<{file: File, preview: string, displayUrl?: string}>>([]);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [imageUrl, setImageUrl] = useState('');

  // 构建资源库图片列表：如果有营业执照，放在第一位
  const resourceImages = React.useMemo(() => {
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    const images = [];
    
    console.log('🖼️ ImageDialog - 构建资源图片列表');
    console.log('  businessLicenseUrl:', businessLicenseUrl);
    
    // 如果有营业执照，添加到第一位
    if (businessLicenseUrl) {
      // 处理相对路径
      const fullUrl = businessLicenseUrl.startsWith('/uploads/') 
        ? `${API_BASE_URL}${businessLicenseUrl}` 
        : businessLicenseUrl;
      images.push(fullUrl);
      console.log('  ✅ 营业执照已添加到第一位:', fullUrl);
    } else {
      console.log('  ⚠️ 没有营业执照URL');
    }
    
    // 添加其他预设图片
    images.push(...RESOURCE_IMAGES);
    console.log('  总图片数:', images.length);
    
    return images;
  }, [businessLicenseUrl]);

  // 重置状态
  useEffect(() => {
    if (show) {
      setActiveTab('upload');  // 默认选择上传tab
      setSelectedResourceImages(new Set());
      setUploadedFiles([]);
      setPreviewImage(null);
      setImageUrl('');
    }
  }, [show]);

  // 切换资源库图片选中状态
  const toggleResourceImage = (index: number) => {
    const newSelected = new Set(selectedResourceImages);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedResourceImages(newSelected);
  };

  // 处理文件上传
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      
      // 上传到后端
      const formData = new FormData();
      files.forEach(file => {
        formData.append('files', file);
      });
      
      try {
        const API_BASE_URL = process.env.REACT_APP_API_URL !== undefined ? process.env.REACT_APP_API_URL : 'http://localhost:8000';
        const response = await fetch(`${API_BASE_URL}/api/content/upload-images`, {
          method: 'POST',
          body: formData,
        });
        
        const result = await response.json();
        
        if (result.success && result.urls) {
          // 使用后端返回的URL
          const newUploads = result.urls.map((url: string, index: number) => ({
            file: files[index],
            preview: `${API_BASE_URL}${url}`,  // 完整URL用于预览
            displayUrl: url,  // 相对路径用于插入Markdown
          }));
          setUploadedFiles([...uploadedFiles, ...newUploads]);
        }
      } catch (error) {
        console.error('图片上传失败:', error);
        alert('图片上传失败，请重试');
      }
      
      // 重置 input 以便可以重复选择相同文件
      e.target.value = '';
    }
  };

  // 删除上传的图片
  const removeUploadedImage = (index: number) => {
    const newUploads = [...uploadedFiles];
    // 不再需要 revokeObjectURL，因为使用的是后端URL
    newUploads.splice(index, 1);
    setUploadedFiles(newUploads);
  };

  // 插入图片
  const handleInsert = () => {
    const urls: string[] = [];

    // 资源tab已隐藏，只处理上传和网图
    if (activeTab === 'upload') {
      // 上传：插入所有上传的图片（使用相对路径，渲染时动态拼接API_BASE_URL）
      uploadedFiles.forEach(upload => {
        if (upload.displayUrl) {
          urls.push(upload.displayUrl);  // 使用相对路径
        }
      });
    } else if (activeTab === 'url') {
      // 网图：直接使用URL
      if (imageUrl.trim()) {
        urls.push(imageUrl.trim());
      }
    }

    if (urls.length === 0) {
      alert('请选择或输入图片');
      return;
    }

    onInsert(urls);
    onClose();
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000] p-4">
      {/* 强制覆盖外部CSS污染 - 与样式设置弹框完全一致 */}
      <style>{`
        div.image-dialog-header,
        div.image-dialog-header * {
          line-height: normal !important;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif !important;
        }
        div.image-dialog-header h3 {
          font-size: 16px !important;
          line-height: 1.5 !important;
          color: #ffffff !important;
          margin: 0 !important;
          padding: 0 !important;
        }
        #image-dialog-desc,
        p#image-dialog-desc,
        div.image-dialog-header p#image-dialog-desc {
          font-size: 10px !important;
          line-height: 1.2 !important;
          color: rgba(191, 219, 254, 1) !important;
          margin: 0 !important;
          margin-top: 2px !important;
          padding: 0 !important;
        }
      `}</style>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* 头部 - 与样式设置弹框保持一致 */}
        <div className="image-dialog-header bg-gradient-to-r from-blue-600 via-blue-500 to-cyan-500 px-5 py-3 flex items-center justify-between">
          <div className="flex items-center space-x-2.5">
            <div className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-lg bg-white/20 backdrop-blur-sm">
              <svg className="w-4.5 h-4.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <h3 className="text-base font-bold text-white">插入图片</h3>
              <p id="image-dialog-desc" className="text-[10px] text-blue-100">选择或生成图片插入到文档</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white/90 hover:text-white hover:bg-white/20 rounded-lg p-1.5 transition-all duration-200"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tab 标签页 - 资源tab暂时隐藏 */}
        <div className="border-b border-gray-200 px-5">
          <div className="flex gap-6">
            <button
              onClick={() => setActiveTab('upload')}
              className={`py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'upload'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-800'
              }`}
            >
              上传
            </button>
            <button
              onClick={() => setActiveTab('url')}
              className={`py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'url'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-800'
              }`}
            >
              网图
            </button>
          </div>
        </div>

        {/* 内容区域 */}
        <div className="flex-1 overflow-auto px-5 py-4">
          {/* 资源Tab - 暂时隐藏 */}
          {/* {activeTab === 'resource' && (...)} */}

          {/* 上传Tab */}
          {activeTab === 'upload' && (
            <div className="space-y-4">
              <div
                className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center hover:border-blue-400 transition-colors cursor-pointer"
                onClick={() => document.getElementById('file-input')?.click()}
              >
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <p className="mt-2 text-sm text-gray-600">
                  点击或拖拽上传图片
                </p>
                <p className="mt-1 text-xs text-gray-500">
                  支持同时选择多张图片（按住 Ctrl/Cmd 多选）
                </p>
                <input
                  id="file-input"
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                />
              </div>
              
              {/* 缩略图列表 */}
              {uploadedFiles.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-3">已上传 {uploadedFiles.length} 张图片</p>
                  <div className="grid grid-cols-5 gap-3">
                    {uploadedFiles.map((upload, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={upload.preview}
                          alt={`上传 ${index + 1}`}
                          className="w-full h-24 object-cover rounded-lg border border-gray-200"
                        />
                        {/* 悬浮操作 */}
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all rounded-lg flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                          {/* 预览 */}
                          <button
                            onClick={() => setPreviewImage(upload.preview)}
                            className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
                            title="预览"
                          >
                            <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </button>
                          {/* 删除 */}
                          <button
                            onClick={() => removeUploadedImage(index)}
                            className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
                            title="删除"
                          >
                            <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 网图Tab */}
          {activeTab === 'url' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                图片链接
              </label>
              <input
                type="text"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="请输入在线图片链接（https://...）"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {imageUrl.trim() && (
                <div className="mt-4">
                  <p className="text-sm text-gray-700 mb-2">预览：</p>
                  <img
                    src={imageUrl}
                    alt="预览"
                    className="max-w-full h-auto rounded-lg border border-gray-200"
                    onError={(e) => {
                      e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23ddd" width="200" height="200"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3E图片加载失败%3C/text%3E%3C/svg%3E';
                    }}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* 底部按钮 */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 text-gray-700 font-bold rounded-lg hover:bg-gray-50 transition-colors"
          >
            取消
          </button>
          <button
            onClick={handleInsert}
            className="px-6 py-2 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 transition-colors"
          >
            插入
          </button>
        </div>
      </div>

      {/* 预览弹窗 */}
      {previewImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[10001]"
          onClick={() => setPreviewImage(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh]">
            <button
              onClick={() => setPreviewImage(null)}
              className="absolute -top-10 right-0 text-white hover:text-gray-300 transition-colors"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <img
              src={previewImage}
              alt="预览"
              className="max-w-full max-h-[90vh] rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageDialog;
